import { of, switchMap } from 'rxjs';
import { MapTankInfo } from './../../interfaces/mapTankInfo';
import {
  Component,
  OnDestroy,
  ViewEncapsulation,
  HostListener,
  ViewChild,
  ElementRef,
  Input,
  AfterViewInit,
} from '@angular/core';
import { AppState } from '../../app.state';
import { Store } from '@ngrx/store';
import { selectFilteredTanks } from '../../state/selectors/filter-profile.selectors';
import { LogService } from '../../services/log.service';
import { GridService } from '../../services/grid.service';
import {AppSettings, mapAlarmColors, tapMapStyles} from '../../shared/Constants/sharedConstants';
import { mapMarkerIcons } from '../../shared/Constants/sharedConstants';
import { take, debounceTime } from 'rxjs/operators';
import { selectTankGridChange, selectTankGridChangeForMap } from 'src/app/state/selectors/tank-profile.selectors';
import { TranslateService } from '@ngx-translate/core';
import { TankService } from '@services/tank.service';
import { selectUserInformation } from 'src/app/state/selectors/user-profile.selectors';
import { MapRegion, mapRegionTypes, MapService, regionStatusCodes } from '@services/map.service';
import { AppDialogService } from '../../services/app-dialog.service';
import { GeolocationFilterModel, TankFilterService } from '../../services/tank-filter.service';
import { ChangeTankFilter } from '../../state/actions/filter.action';
import { TankSetupService, TankDetailsPhotoDto } from '@services/tank-setup.service';

declare let MarkerClusterer: any;

@Component({
  selector: 'tank-map',
  templateUrl: './tank-map.component.html',
  styleUrls: ['./tank-map.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class TankMapComponent implements OnDestroy, AfterViewInit {

  @Input()
  get mapPanelIsClosed(): boolean {
    return this._mapPanelIsClosed;
  }
  set mapPanelIsClosed(flag: boolean) {
    this._mapPanelIsClosed = flag;
    if (this.mapNeedsUpdated === true && this._mapPanelIsClosed === false) {
      // map is now being shown and the map needs updated, then do so
      this.setupMapDataSubscription();
    }
  }

  @Input()
  get showMapClusters(): boolean {
    return this._showMapClusters && !this.mapHasGeolocationMapFilters;
  }
  set showMapClusters(flag: boolean) {
    this._showMapClusters = flag;
    if (flag && this.markerClusterer) {
      this.markerClusterer.setMaxZoom(
        this._showMapClusters === true
          ? this.showMaxClusterZoom
          : this.hideMaxClusterZoom,
      );
      this.markerClusterer.repaint();
    }
    else {
      // Bypass clustering by forcing a max zoom that is lower than the zoom level at which clusters break apart
      this.markerClusterer?.setMaxZoom(this.hideMaxClusterZoom);
      this.markerClusterer?.repaint();
    }
  }

  @Input()
  get uniqueId(): number {
    return this._uniqueId;
  }
  set uniqueId(flag: number) {
    this._uniqueId = flag;
    this._isSingleTank = this._uniqueId === 0 ? false : true;
  }

  _useMapRegions: boolean = false;
  @Input()
  get useMapRegions(): boolean {
    return this._useMapRegions;
  }
  set useMapRegions(flag: boolean) {
    this._useMapRegions = flag;
  }

  @Input()
  hideInfoIcon: boolean = false;

  /**
   * The Show Map Clusters / region-drawing toolbar, which floats over the map's
   * top-left corner. The card reads the same flag to drop below it - one source,
   * so the two cannot drift apart and start overlapping again.
   */
  get showMapToolbar(): boolean {
    return !this.hideInfoIcon || this.useMapRegions;
  }

  @ViewChild('mapContainer') gmap: ElementRef;

  map: google.maps.Map = null;
  bounds: any;
  resources;
  mapNeedsUpdated = false;
  gridChange: any;

  address: any;
  cityStateZip: any;
  volumePct: any;
  currentTankPhotos: TankDetailsPhotoDto[] = [];

  initMap: boolean = false;

  mapOptions: google.maps.MapOptions = {
    mapTypeControl: false,
    disableDefaultUI: false,
    panControl: true,
    zoomControl: true,
    scaleControl: true,
    streetViewControl: false,
    rotateControl: false,
    maxZoom: 18,
    minZoom: 2,
    styles: tapMapStyles,
    zoom: 1,
  };

  markerClusterer: any;
  // Each style needs a url: MarkerClustererPlus always renders an <img>, and without
  // one it emits src="undefined" and 404s. The visible icon is drawn entirely by
  // .custom-clustericon in styles.scss, so a 1x1 transparent pixel is all that is needed.
  markerClusterStyles = [
    {
      url: 'assets/images/pixel.png',
      textColor: 'white',
      height: 50,
      width: 50,
      className: 'custom-clustericon-1',
    },
    {
      url: 'assets/images/pixel.png',
      textColor: 'white',
      height: 60,
      width: 60,
      className: 'custom-clustericon-2',
    },
    {
      url: 'assets/images/pixel.png',
      textColor: 'white',
      height: 70,
      width: 70,
      className: 'custom-clustericon-3',
    },
  ];

  markersWithTankIds: any[] = [];

  private _showMapClusters = true;
  private _mapPanelIsClosed = true;
  private _uniqueId = 0;
  public _isSingleTank = false;
  private subscription;
  private tankSubscription;

  private activeInfoWindow: any;
  private carouselMarkers: any;
  public carouselIndex = 0;
  public carouselCount = 0;

  public currentTankInfo: MapTankInfo = null;

  private zIndex = 0;
  private labelOrigin: any;
  private tankIndex = -1;
  private carouselStartIndex = -1;
  private carouselEndIndex = -1;
  private carouselTanks: any[];
  private showMaxClusterZoom = 14;
  private hideMaxClusterZoom = -1;

  // geolocation and map drawing data
  private geolocationMapRegion: MapRegion = null;
  private geolocationId: number = 0;
  public showCustomControls = { draw: false, edit: false, apply: false, cancel: false, delete: false, latLon: false, radius: false, applyNew: false, cancelNew: false }
  public currentLatLon = new google.maps.LatLng(0, 0);
  public currentRadius = 0;
  private drawingManager: google.maps.drawing.DrawingManager = null;
  private newCircle: google.maps.Circle = null;

    constructor(
      private store: Store<AppState>,
      private logger: LogService,
      private _gridService: GridService,
      private _tankService: TankService,
      public translate: TranslateService,
      private _mapService: MapService,
      private _ads: AppDialogService,
      private _tfs: TankFilterService,
      private _tankSetupService: TankSetupService,
    ) {}

  //respond to window resize
  @HostListener('window:resize', ['$event'])
  onResize(event) {
    //Recenter and zoom the map based on the bounds (selected/listed tanks).
    this.recenterAndZoomMap();
  }

  ngAfterViewInit() {
    if (this.map) {
      return;
    }
    window['mapGoogleHelper'] = {
      tankMapComponent: this,
    };

    // if this map is for a specific tank, call the correct service code to pull the tank
    if (this._isSingleTank) this.loadSingleTankData();
    else this.loadTankData();
  }

  loadSingleTankData() {
    this._tankService
      .getMapDataByUniqueId(this._uniqueId)
      .subscribe((payload) => {
        if (!payload) {
          // if there are no tanks, clear any previous markers and bail
          this.clearAllMarkers();
          return;
        }
        this.displayMap(payload);
      });
  }

  loadTankData() {
    this.store
      .select(selectUserInformation)
      .pipe(take(1))
      .subscribe((userInfo) => {
        if (userInfo) {
          //subscribe to Grid Filter updates
          if (!this.tankSubscription)
            this.setupMapDataSubscription();
        }
      });
  }

  updateMap(data: any) {
    if (!data) {
      // if there are no tanks, clear any previous markers and bail
      this.clearAllMarkers();
      return;
    }
    this.displayMap(data);
  }

  clearAllMarkers() {
    this.markersWithTankIds = [];
    this.carouselTanks = [];
    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers();
    }

  }

  displayMap(mapRows: any[]) {
    if (!this.map) {
      this.map = new google.maps.Map(this.gmap.nativeElement, this.mapOptions);
      if (this.useMapRegions === true) {
        this.drawingManager = this._mapService.createDrawingManager(this.map);
        // attached once, at creation - attaching it per drawing-mode change stacks a
        // fresh handler on every arm and completes the circle N times
        // Dispatched from the root zone - see MapService.runInZone.
        this.drawingManager.addListener('circlecomplete', (circle) =>
          this._mapService.runInZone(() => this.circleComplete(circle)),
        );
      }
      this.labelOrigin = new google.maps.Point(16, -6);
    }

    // remove any geolocation filters
    if (this.useMapRegions === true) {
      this.clearAllMapRegions();
      this.showCustomControls.draw = !this.mapHasGeolocationMapFilters;

      if (this.map && this.mapHasGeolocationMapFilters)
        this.showGeolocationMapRegion();
    }

    this.markersWithTankIds = [];
    this.carouselTanks = [];
    this.bounds = null;
    this.zIndex = 0;
    this.tankIndex = 0;

    this.bounds = new google.maps.LatLngBounds();

    // loop thru the tanks and create the map marker
    mapRows.forEach((tank) => {
      // if we do not have a carousel in play, see if this tank is the beginning of one
      if (this.carouselStartIndex === -1) {
        // look ahead and see if this tank is part of a carousel
        if (
          this.tankIndex + 1 < mapRows.length &&
          tank.latitude === mapRows[this.tankIndex + 1].latitude &&
          tank.longitude === mapRows[this.tankIndex + 1].longitude
        ) {
          // this tank starts a new carousel
          this.carouselStartIndex = this.tankIndex;
          this.carouselEndIndex = this.tankIndex;
          // now find the end of the carousel
          while (
            this.carouselEndIndex + 1 < mapRows.length &&
            tank.latitude === mapRows[this.carouselEndIndex + 1].latitude &&
            tank.longitude === mapRows[this.carouselEndIndex + 1].longitude
          ) {
            this.carouselEndIndex++;
          }
        }
      }

      // if this tank is part of a carousel, add it to the carousel array
      if (this.carouselEndIndex > -1) {
        this.carouselTanks.push(tank);
      }

      const marker = this.createTankMapMarker(tank);

      //bounds used for centering/zooming
      this.bounds.extend(marker.getPosition());

      //add to the array to return
      this.markersWithTankIds.push(marker);

      // if we are on the last tank for the carousel, reset carousel indeces
      if (this.tankIndex === this.carouselEndIndex) {
        this.carouselEndIndex = -1;
        this.carouselStartIndex = -1;
      }

      this.tankIndex++;
    });

    setTimeout(() => this.recenterAndZoomMap(), 500);

    this.setMapScaleUOM();

    //setup and get the Filtered tanks from the state store
    this.subscription = this.store
      .select(selectFilteredTanks)
      .subscribe((tanksToExclude: number[]) => {
        if (!tanksToExclude) {
          return;
        }
        this.excludeTanksFromMap(tanksToExclude);
      });

    // add custom drawing controls
    if (this.mapHasGeolocationMapFilters) {
      // an existing map region is present
      this.hideDrawingManager();
      this.showEditMode();
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.tankSubscription) {
      this.tankSubscription.unsubscribe();
    }
  }

  recenterAndZoomMap() {
    // any geolocation map filter overrides other bounds
    const bounds = this.mapHasGeolocationMapFilters
      ? this.geolocationMapRegion.getMapRegionBounds()
      : this.bounds;

    this._mapService.frameBounds(this.map, bounds, { panIntoView: true });
  }

  translateHeader(key) {
    if (!this.resources) {
      return key;
    } else if (!this.resources[key]) {
      this.logger.log(`Could not find StringResource: ${key}`);
      return key;
    }

    return this.resources[key];
  }

  excludeTanksFromMap(tanksToExclude: number[]) {
    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers();
    }
    if (!this.markersWithTankIds || this.markersWithTankIds.length === 0) {
      return;
    }

    this.markersWithTankIds.forEach((marker) => {
      marker.setVisible(
        !tanksToExclude ||
        tanksToExclude.indexOf(marker.tankInfo['tankID']) === -1,
      );
    });

    //reset marker clusters
    const markersToCluster = this.markersWithTankIds.filter(
      (mm) => mm['visible'] === true,
    );

    this.markerClusterer = new MarkerClusterer(this.map, markersToCluster, {
      styles: this.markerClusterStyles,
      clusterClass: 'custom-clustericon',
      maxZoom:
        this.showMapClusters === true
          ? this.showMaxClusterZoom
          : this.hideMaxClusterZoom,
    });

    this.markerClusterer.repaint();
  }

  createTankMapMarker(tank: any): any {
    const coordinates = new google.maps.LatLng(tank.latitude, tank.longitude);

    let label = null;
    // if this tank is part of a carousel, add a label with the carousel tank count
    if (this.carouselEndIndex === this.tankIndex) {
      label = {
        text: (this.carouselEndIndex - this.carouselStartIndex + 1).toString(),
        className: 'mapMarkerLabel',
      };
    }

    const marker = new google.maps.Marker({
      position: coordinates,
      icon: {
        fillColor: mapAlarmColors[tank.volumeAlarmStatusType],
        url: '../../assets/svg/' + mapMarkerIcons[tank?.showAsInactive ? 3 :  tank.volumeAlarmStatusType],
        labelOrigin: this.labelOrigin,
      },
      label,
      optimized: false,
      zIndex: this.zIndex,
    });
    marker['tankInfo'] = tank;
    // Dispatched from the root zone - see MapService.runInZone. Re-entering here rather
    // than in the subscribe callback is what puts the getMapDataExpanded request inside
    // the zone, so its response ticks change detection and the card renders on arrival.
    marker.addListener('click', () =>
      this._mapService.runInZone(() => {
        if (this.activeInfoWindow) {
          this.activeInfoWindow.close();
          this.activeInfoWindow = null;
        }

        if (this._isSingleTank === false) this.createInfoWindow(marker);
      }),
    );

    this.zIndex++;
    return marker;
  }
  createInfoWindow(marker: any) {
    let ti = marker.tankInfo;

    this.carouselMarkers = this.carouselTanks.filter(
      (t) =>
        t.latitude === marker.tankInfo.latitude &&
        t.longitude === marker.tankInfo.longitude,
    );
    const needsCarousel: boolean =
      this.carouselMarkers.length > 1 ? true : false;

    if (needsCarousel) {
      // sort neighboring markers is reverse critical order so the carousel will show most critical tanks first
      this.carouselMarkers = this.carouselMarkers.sort((a, b) => {
        if (a.volumeAlarmStatusType < b.volumeAlarmStatusType) {
          return 1;
        }
        if (a.volumeAlarmStatusType > b.volumeAlarmStatusType) {
          return -1;
        }
        if (a.uniqueID < b.uniqueID) {
          return 1;
        }
        if (a.uniqueID > b.uniqueID) {
          return -1;
        }
        return 0;
      });
      this.carouselIndex = 0;
      this.carouselCount = this.carouselMarkers.length;
      ti = this.carouselMarkers[this.carouselIndex];
    }
    //reset carousel indeces
    else {
      this.carouselIndex = 0;
      this.carouselCount = 0;
    }

    this.logger.trackEvent(
      'TankMap',
      'click',
      'ShowMapPopUp',
      marker.tankInfo.tankID,
    );

    //expand on the tank data we have to display in the map card
    this._gridService.getMapDataExpanded(ti).subscribe((mapTankInfo) => {
      this.currentTankInfo = mapTankInfo;
      this.setInfoWindowContent();
      this.loadCardPhotos(mapTankInfo);
    });
  }

  /**
   * MapTankInfo carries only the primary photo url, so the card's carousel
   * needs the full set. Cleared first: the card is reused across markers and
   * must never show the previous tank's photos while this call is in flight.
   */
  private loadCardPhotos(tankInfo: MapTankInfo) {
    if (this.currentTankPhotos.length) this.currentTankPhotos = [];
    if (!tankInfo?.uniqueID) return;

    const requestedFor = tankInfo.uniqueID;
    this._tankSetupService
      .getPhotos(requestedFor)
      .pipe(take(1))
      .subscribe({
        next: (photos) => {
          // a later marker click may have landed while this was in flight
          if (this.currentTankInfo?.uniqueID !== requestedFor) return;
          this.currentTankPhotos = photos ?? [];
        },
        error: () => {
          if (this.currentTankInfo?.uniqueID !== requestedFor) return;
          this.currentTankPhotos = [];
        },
      });
  }

  setInfoWindowContent() {
    // parse formatted address from lat/lon data
    //const comma = this.currentTankInfo.formattedAddress.indexOf(',');
    // const address: string =
    //   comma <= 0
    //     ? this.currentTankInfo.formattedAddress
    //     : this.currentTankInfo.formattedAddress.substr(0, comma + 1);
    // const cityStateZip: string =
    //   comma <= 0 ? '' : this.currentTankInfo.formattedAddress.substr(comma + 1);
    const volumePct: number = Math.round(
      this.currentTankInfo.inventoryPercent * 100,
    );

    //this.address = address;
    //this.cityStateZip = cityStateZip;
    this.volumePct = volumePct;
  }

  prevCarouselMarker() {
    // user selects previous carousel info window
    this.carouselIndex--;
    if (this.carouselIndex < 0) {
      this.carouselIndex = this.carouselCount - 1;
    }
    this.updateCarouselInfoWindow();
    this.logger.trackEvent('TankMap', 'click', 'PrevMapCarousel');
  }

  nextCarouselMarker() {
    // user selects previous carousel info window
    this.carouselIndex++;
    if (this.carouselIndex >= this.carouselCount) {
      this.carouselIndex = 0;
    }
    this.updateCarouselInfoWindow();
    this.logger.trackEvent('TankMap', 'click', 'NextMapCarousel');
  }

  updateCarouselInfoWindow() {
    // get tank info window data from server
    const tankInfo = this.carouselMarkers[this.carouselIndex];
    this._gridService.getMapDataExpanded(tankInfo).subscribe((mapTankInfo) => {
      this.currentTankInfo = mapTankInfo;
      // update info window
      this.setInfoWindowContent();
      this.loadCardPhotos(mapTankInfo);
      this.logger.trackEvent(
        'TankMap',
        'click',
        'ShowMapCarousel',
        this.currentTankInfo.tankID,
      );
    });
  }

  toggleShowMapClusters($event) {
    this.showMapClusters = !this.showMapClusters;
    this.logger.trackEvent(
      'TankMap',
      'click',
      'ShowMapMarker',
      this.showMapClusters ? 0 : 1,
    );
  }

  onPopUpCloseClick() {
    this.closeTankCard();
    this.logger.trackEvent('TankMap', 'click', 'CloseMapInfoWindow');
  }

  /**
   * Clearing currentTankInfo destroys the card and, with it, the Syncfusion
   * daily-usage chart inside it. The old popup only detached its container from
   * the map pane, so both stayed mounted for the life of the page.
   */
  private closeTankCard() {
    this.currentTankInfo = null;
    // a fresh array every time would be a new binding value on every close
    if (this.currentTankPhotos.length) this.currentTankPhotos = [];
    this.carouselIndex = 0;
    this.carouselCount = 0;
  }

  mapAPIReady() {
    if (this.initMap === false && this._mapService.mapIsInitialized) {
      this.initMap = true;
    }
    return this.initMap;
  }

  setupMapDataSubscription() {
    this.tankSubscription = this.store
    .select(selectTankGridChangeForMap)
    .pipe(
      debounceTime(AppSettings.debounceTime),
      switchMap((g) => {
        this.gridChange = g;

        //detected a change in the user's profile.
        // remove any existing elements from the map before updating
        this.closeTankCard();
        this.clearAllMarkers();

        // if the map panel is closed, set a flag to indicate the map needs to be updated when next shown
        if (this.mapPanelIsClosed === true) {
          this.mapNeedsUpdated = true;
          return of(null);
        }

        this.mapNeedsUpdated = false;
        return this._gridService.getMapData(this.gridChange);
      }),
    )
      .subscribe((data) => {
      this.updateMap(data);
    });

  }

  //========================================================
  // geolocation and map drawing
  //========================================================

  showGeolocationMapRegion() {
    // allow only one geolocation filter to be active
    this.clearAllMapRegions();

    const filterValues = this.gridChange.filterModel.geoLocationFilter.values;
    if (filterValues.length < 4) return;

    this.geolocationId = +filterValues[0];
    let regionType: mapRegionTypes = mapRegionTypes.LOCATION_BASED;

    // create the map region based on the 1st filter value
    switch (true) {
      case (this.geolocationId == 0): // lat/lon based
        regionType = mapRegionTypes.LATLON_BASED;
      case (this.geolocationId > 0): // location based
        const circleRadius: number = +filterValues[1];
        const centerLat: number = +filterValues[2];
        const centerLon: number = +filterValues[3];
        const opts = {
          centerLat: centerLat,
          centerLon: centerLon,
          radius: circleRadius,
        };
        this.geolocationMapRegion = new MapRegion(this.map, regionType, regionStatusCodes.COMPLETED, opts);
        this.updateCircleStatus(this.geolocationMapRegion.circleMapRegion);
        break;
      default:
        this.geolocationMapRegion = null;
    }

    if (this.geolocationMapRegion == null)
      return;
  }

  get mapHasGeolocationMapFilters(): boolean {
    return (this.useMapRegions == true && this.gridChange != null && this.gridChange.filterModel != null && this.gridChange.filterModel.geoLocationFilter != null);
  }

  hideDrawingManager() {
    this.stopDrawMode();
    this.showCustomControls.draw = false;
  }

  onDrawClick() {
    this.startDrawingRegion();
  }

  // Deliberately does not touch clustering. hideMaxClusterZoom is -1, so turning
  // clusters off renders every tank as its own unoptimized marker and hangs the browser
  // on large tenants. Clusters stop showing on their own once a region is applied,
  // because the showMapClusters getter masks them while a geolocation filter is present.
  startDrawingRegion() {
    this.startDrawMode();
  }

  get isInDrawMode(): boolean {
    return this._mapService.isDrawingModeActive(this.drawingManager);
  }

  startDrawMode() {
    if (this.drawingManager) this._mapService.enableDrawingMode(this.drawingManager);
  }

  stopDrawMode() {
    if (this.drawingManager) this._mapService.disableDrawingMode(this.drawingManager);
  }

  onEditClick() {
    var circle = this.geolocationMapRegion.circleMapRegion;
    this.enterEditMode();
    this.updateCircleStatus(circle);
    this.stopDrawMode();
    this.showCustomControls.draw = false;

    this._mapService.setCircleColors(circle, false);
    circle.setOptions({ clickable: true, draggable: true, editable: true })

    // Dispatched from the root zone - see MapService.runInZone. These drive the lat/lon
    // and radius readouts in the map toolbar.
    circle.addListener('center_changed', () =>
      this._mapService.runInZone(() => this.updateCircleStatus(circle)),
    );

    circle.addListener('radius_changed', () =>
      this._mapService.runInZone(() => this.updateCircleStatus(circle)),
    );

    circle.addListener('drag', () =>
      this._mapService.runInZone(() => this.updateCircleStatus(circle)),
    );
  }

  onCancelClick() {
    //// per Marcia, remove all confirmation dialogs
    //this._ads.showConfirmationDialog(
    //  this.translate.instant('TankMap.CustomControls.MapRegion'),
    //  this.translate.instant('TankMap.CustomControls.ConfirmCancel'),
    //)
    //  .pipe(take(1)).subscribe((confirmed) => {
    //    if (!confirmed) return;
    //    this.showGeolocationMapRegion();
    //    this.showEditMode();
    //  });

    // cancel the current circle map region
    this.showGeolocationMapRegion();
    this.showEditMode();
}

  onApplyClick() {
    //// per Marcia, remove all confirmation dialogs
    //this._ads.showConfirmationDialog(
    //  this.translate.instant('TankMap.CustomControls.MapRegion'),
    //  this.translate.instant('TankMap.CustomControls.ConfirmApply'),
    //)
    //  .pipe(take(1)).subscribe((confirmed) => {
    //    if (confirmed)
    //      this.applyCircleMapRegion();
    //  });
    //return;

    // apply changes made to the map region
    this.applyCircleMapRegion();
  }

  applyCircleMapRegion() {
    // save the new circle as the new geolocation filter
    const circle = this.geolocationMapRegion.circleMapRegion;
    this._mapService.setCircleColors(circle, false);
    circle.setOptions({ clickable: false, draggable: false, editable: false })
    this.resetDrawingManager();

    // update the filter model
    this.updateCircleStatus(circle);
    const values = ['0', this.currentRadius.toString(), this.currentLatLon.lat().toString(), this.currentLatLon.lng().toString()];
    var geoLocationFilter = { filterType: 'set', values: values }
    var fm = { ...this.gridChange.filterModel, geoLocationFilter };
    this._tfs.applyFilterModelToState(fm, this.constructor.name, false);

    this.showEditMode();
  }


  onDeleteClick() {
    //// per Marcia, remove all confirmation dialogs
    //this._ads.showConfirmationDialog(
    //  this.translate.instant('TankMap.CustomControls.MapRegion'),
    //  this.translate.instant('TankMap.CustomControls.ConfirmDelete'),
    //)
    //  .pipe(take(1)).subscribe((confirmed) => {
    //    if (confirmed)
    //      this.deleteCircleMapRegion();
    //  });
    //return;

    // delete the map region
    this.deleteCircleMapRegion();
  }

  deleteCircleMapRegion() {
    // delete the map region and update the geolocation filter
    this.clearAllMapRegions();
    this.geolocationMapRegion = null;
    this.exitEditMode();
    this.stopDrawMode();
    this.showCustomControls.draw = true;

    // remove the geolocation filter model
    var fm = { ...this.gridChange.filterModel };
    delete fm['geoLocationFilter']
    this._tfs.applyFilterModelToState(fm, this.constructor.name, false);
    // re-assert the user's own cluster preference now that the geolocation
    // filter is gone; the assignment also repaints the clusterer
    this.showMapClusters = this._showMapClusters;
  }

  onApplyNewClick() {
    //// per Marcia, remove all confirmation dialogs
    //this._ads.showConfirmationDialog(
    //  this.translate.instant('TankMap.CustomControls.MapRegion'),
    //  this.translate.instant('TankMap.CustomControls.ConfirmApply'),
    //)
    //  .pipe(take(1)).subscribe((confirmed) => {
    //    if (confirmed)
    //      this.applyNewCircleMapRegion();
    //  });
    //return;

    // apply the new circle as the new geolocation filter
    this.applyNewCircleMapRegion();

  }

  applyNewCircleMapRegion() {
    // save the new circle as the new geolocation filter

    this._mapService.setCircleColors(this.newCircle, false);
    this.newCircle.setOptions({ clickable: false, draggable: false, editable: false })
    this.resetDrawingManager();

    this.clearAllMapRegions();

    // create a new map region to replace any existing ones
    this.geolocationMapRegion = new MapRegion(this.map, mapRegionTypes.LATLON_BASED, regionStatusCodes.COMPLETED, null, this.newCircle);
    this.newCircle = null;

    // update the filter model
    const values = ['0', this.currentRadius.toString(), this.currentLatLon.lat().toString(), this.currentLatLon.lng().toString()];
    var geoLocationFilter = {filterType: 'set', values: values }
    var fm = { ...this.gridChange.filterModel, geoLocationFilter };
    this._tfs.applyFilterModelToState(fm, this.constructor.name, false);

    this.showEditMode();
  }

  onCancelNewClick() {
    //// per Marcia, remove all confirmation dialogs
    //  this._ads.showConfirmationDialog(
    //  this.translate.instant('TankMap.CustomControls.MapRegion'),
    //  this.translate.instant('TankMap.CustomControls.ConfirmCancel'),
    //)
    //  .pipe(take(1)).subscribe((confirmed) => {
    //    if (!confirmed) return;
    //    this.newCircle.setMap(null);
    //    this.newCircle = null;
    //    this._mapService.exitDrawingMode(this.drawingManager);
    //    this.exitEditMode();
    //    this.showMapClusters = this.savedMapClustersMode;
    //  });

    // cancel the newly drawn circle
    this.newCircle.setMap(null);
    this.newCircle = null;
    this.stopDrawMode();
    this.showCustomControls.draw = true;
    this.exitEditMode();
  }

  resetDrawingManager() {
    this.hideDrawingManager();
    this.showCustomControls.applyNew = false;
    this.showCustomControls.cancelNew = false;
    this.showCustomControls.latLon = false;
    this.showCustomControls.radius = false;
  }

  showEditMode() {
    this.showCustomControls.edit = true;
    this.showCustomControls.applyNew = false;
    this.showCustomControls.cancelNew = false;
    this.showCustomControls.apply = false;
    this.showCustomControls.cancel = false;
    this.showCustomControls.delete = false;
    this.showCustomControls.latLon = true;
    this.showCustomControls.radius = true;
  }

  enterEditMode() {
    this.showCustomControls.edit = false;
    this.showCustomControls.applyNew = false;
    this.showCustomControls.cancelNew = false;
    this.showCustomControls.apply = true;
    this.showCustomControls.cancel = true;
    this.showCustomControls.delete = true;
    this.showCustomControls.latLon = true;
    this.showCustomControls.radius = true;
  }

  exitEditMode() {
    this.showCustomControls.edit = false;
    this.showCustomControls.applyNew = false;
    this.showCustomControls.cancelNew = false;
    this.showCustomControls.apply = false;
    this.showCustomControls.cancel = false;
    this.showCustomControls.delete = false;
    this.showCustomControls.latLon = false;
    this.showCustomControls.radius = false;
  }

  updateCircleStatus(circle: google.maps.Circle) {
    this.currentLatLon = this._mapService.getCircleCenter(circle);
    this.currentRadius = this._mapService.getCircleRadius(circle);
  }

  //showCircleStatusText(tf: boolean) {
  //  this.showCustomControls.latLon = tf;
  //  this.showCustomControls.radius = tf;
  //}

  clearAllMapRegions() {
    // remove any existing map region
    if (this.geolocationMapRegion) {
      this.geolocationMapRegion.circleMapRegion.setMap(null);
    }

    if (this.newCircle) {
      this.newCircle.setMap(null);
    }

    this.exitEditMode();

  }

  circleComplete(circle: google.maps.Circle) {
    // we now have a circle region object
    this.updateCircleStatus(circle);

    this.hideDrawingManager();
    setTimeout(() => {
      this.showCustomControls.applyNew = true;
      this.showCustomControls.cancelNew = true;
    }, 250);

    circle.setOptions({ clickable: true, draggable: true, editable: true })

    // Dispatched from the root zone - see MapService.runInZone. These drive the lat/lon
    // and radius readouts in the map toolbar.
    circle.addListener('center_changed', () =>
      this._mapService.runInZone(() => this.updateCircleStatus(circle)),
    );

    circle.addListener('radius_changed', () =>
      this._mapService.runInZone(() => this.updateCircleStatus(circle)),
    );

    circle.addListener('drag', () =>
      this._mapService.runInZone(() => this.updateCircleStatus(circle)),
    );

    this.newCircle = circle;
  }

  setMapScaleUOM() {
    //START hack to set scale to miles/imperial instead of km/metric:
    window.setTimeout(function () {
      var spn = document.getElementById("map").getElementsByClassName("gm-style-cc");
      for (var i in spn) {
        //look for km or m in innerText via regex https://regexr.com/3hiqa
        if ((/\d\s?(km|(m\b))/g).test(spn[i].innerHTML)) {
          const clickableElement = spn[i] as HTMLElement;
          clickableElement.click();
        }
      }
    }, 1000);
    //END hack to set scale to miles/imperial instead of km/metric

  }

}
